<template>
    <section class="mt-6 flex w-full flex-col">
        <div class="flex w-full items-center justify-between">
            <button
                type="button"
                class="lg:hidden"
                @click="appStore.side = !appStore.side"
            >
                <SvgIcon
                    name="menu"
                    class="h-6 w-6"
                />
            </button>
            <div class="mr-auto ml-4 hidden items-center gap-3 md:flex lg:mr-0 lg:ml-0">
                <HeaderNavTitle
                    :show-history-back-button="showHistoryBackButton"
                    :show-return-to-order-link="showReturnToOrderLink"
                    :return-to-order-route="returnToOrderRoute"
                    :header-title="headerTitle"
                    :order-ref="order.getOrderRef"
                    :order-state="order.data?.state"
                    :order-status-formatted="order.data?.status_formatted"
                    @go-back="goBackToPreviousPage"
                    @set-active-tab="setOrderViewActiveTabOnReturn"
                />
            </div>
            <div class="relative flex w-fit shrink-0 items-center gap-x-4">
                <template v-if="userStore.isTenantContext">
                    <RoleGuard :guarded="['ADMIN', 'SALES', 'OPS', 'MANAGER', 'FINANCE', 'OWNER', 'SUPER']">
                        <BookingStatus />
                        <CommandPalette />
                        <router-link
                            class="relative items-center gap-x-1 lg:flex"
                            to="/inbox/calls"
                        >
                            <SvgIcon
                                name="system-missed-call"
                                class="text-muval-gray-1 hover:text-muval-gray-2 size-6"
                            />
                            <div
                                v-show="inboxCounts.phone > 0"
                                class="absolute -top-3 -right-3"
                            >
                                <span
                                    :class="[
                                        'bg-brand z-1 flex items-center justify-center text-xs text-white',
                                        inboxCounts.phone >= 100 ?
                                            'h-[18px] min-w-[24px] rounded-full px-1'
                                        :   'size-[22px] rounded-full',
                                    ]"
                                    v-text="inboxCounts.phone"
                                />
                            </div>
                        </router-link>
                        <router-link
                            class="relative items-center gap-x-1 lg:flex"
                            to="/inbox/sms"
                        >
                            <SvgIcon
                                name="system-inbox"
                                class="text-muval-gray-1 hover:text-muval-gray-2 size-6"
                            />
                            <div
                                v-show="inboxCounts.text > 0"
                                class="absolute -top-3 -right-3"
                            >
                                <span
                                    :class="[
                                        'bg-brand z-1 flex items-center justify-center text-xs text-white',
                                        inboxCounts.text >= 100 ?
                                            'h-[18px] min-w-[24px] rounded-full px-1'
                                        :   'size-[22px] rounded-full',
                                    ]"
                                    v-text="inboxCounts.text"
                                />
                            </div>
                        </router-link>
                        <button
                            type="button"
                            class="relative items-center gap-x-1 lg:flex"
                            :aria-label="
                                salesQueuePaused ? 'My order actions — a paused queue is waiting' : 'My order actions'
                            "
                            @click="myOrderActionsOpen = true"
                        >
                            <SvgIcon
                                name="system-pace"
                                class="text-muval-gray-1 hover:text-muval-gray-2 size-5"
                            />
                            <!--
                                A paused queue is otherwise invisible: its footer
                                is gone by definition, and this panel is the only
                                door back to it. Marked only while paused — while
                                it is running the bar is already on screen, and a
                                second signal for the same queue would just be
                                noise.

                                Amber and bottom-left, so it cannot be read as, or
                                collide with, the brand unread count top-right.
                            -->
                            <span
                                v-if="salesQueuePaused"
                                class="bg-muval-amber absolute -bottom-1 -left-1 size-2.5 rounded-full ring-2 ring-white"
                                data-test="queue-paused-marker"
                            />
                            <div
                                v-show="visitorUnreadCount > 0"
                                class="absolute -top-3 -right-3"
                            >
                                <span
                                    :class="[
                                        'bg-brand z-1 flex items-center justify-center text-xs text-white',
                                        visitorUnreadCount >= 100 ?
                                            'h-[18px] min-w-[24px] rounded-full px-1'
                                        :   'size-[22px] rounded-full',
                                    ]"
                                    v-text="visitorUnreadCount"
                                />
                            </div>
                        </button>
                    </RoleGuard>
                    <NotificationIcon />
                </template>
                <!-- Profile dropdown -->
                <ProfileDropDown />
                <UserInactiveModal v-if="!userStore.isMoveproSuperAdminUser" />
                <UserActivityMonitor v-if="!userStore.isMoveproSuperAdminUser" />
            </div>
        </div>
        <MyOrderActionsModal v-model="myOrderActionsOpen" />
        <div class="mt-4 mr-auto flex w-full items-center gap-3 md:hidden">
            <HeaderNavTitle
                :show-history-back-button="showHistoryBackButton"
                :show-return-to-order-link="showReturnToOrderLink"
                :return-to-order-route="returnToOrderRoute"
                :header-title="headerTitle"
                :order-ref="order.getOrderRef"
                :order-state="order.data?.state"
                :order-status-formatted="order.data?.status_formatted"
                @go-back="goBackToPreviousPage"
                @set-active-tab="setOrderViewActiveTabOnReturn"
            />
        </div>

        <div
            id="new-top-header-bottom"
            class="w-full"
        />
    </section>
</template>
<script setup>
import { onMounted } from 'vue';
import { startCase } from 'lodash';
import { useOrderStore } from '@store/order';
import { useNotificationsStore } from '@store/notifications';
import { useInboxCountsStore } from '@store/inboxCounts';
import { capitalize } from '@utilities/filters';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { useSalesQueue } from '@composables/useSalesQueue';

const appStore = useAppStore();
const userStore = useUserStore();
const jobStore = useJobStore();
const tenderStore = useTenderStore();
const notificationsStore = useNotificationsStore();
const inboxCounts = useInboxCountsStore();

const visitorUnreadCount = computed(() => notificationsStore.getVisitorUnreadCount);

const breakpoints = useBreakpoints(breakpointsTailwind);
const desktopView = breakpoints.greaterOrEqual('sm');

const myOrderActionsOpen = ref(false);

/**
 * Shares `SalesQueueFooter`'s query key, so the two read one request rather than
 * two — and gated the same way, for the same reason: restricted field staff have
 * no use for a sales queue. Not a 403 avoidance — `orders-crud` does grant
 * FIELD_STAFF and CONTRACTOR. See the note on `canHoldQueue` in SalesQueueFooter.
 */
const { isPaused: salesQueuePaused } = useSalesQueue(computed(() => !userStore.isRestrictedFieldStaff?.()));

watch(
    () => userStore.getTeam,
    () => {
        if (userStore.isCentralContext || userStore.isOnlyFieldStaff()) {
            return;
        }

        inboxCounts.fetchCounts();
    },
);

onMounted(() => {
    if (userStore.isCentralContext || userStore.isOnlyFieldStaff()) {
        return;
    }

    notificationsStore.fetchUnreadCount('visitor');
    inboxCounts.fetchCounts();
    // On page load setTenant runs before app.use(echo), so the store's
    // listener guard bails there — (re)start it here where echo exists.
    inboxCounts.startListener();
});

const route = useRoute();
const router = useRouter();

const order = useOrderStore();
const migrationStore = useMigrationStore();

const headerTitle = computed(() => {
    switch (route.name) {
        case 'Dashboard':
            // return userStore.getFirstName ? `Hi ${userStore.getFirstName}!` : null;
            return 'Dashboard';
        // Only mixed-role users reach Home under this header; it matches their sidebar label
        case 'Home':
            return 'Crew home';
        case 'Search':
            return `Search ${userStore.search ?? ''}`;
        // Interstate
        case 'InterstateTripCreate':
            return 'Create interstate trip';
        case 'InterstateTripEdit':
            return 'Edit interstate trip';
        case 'InterstateLaneCreate':
            return 'Create interstate lane';
        case 'InterstateLaneEdit':
            return 'Edit interstate lane';
        // Depots
        case 'DepotsCreate':
            return 'Create depot';
        case 'DepotsEdit':
            return `Edit depot`;
        // Local
        case 'LocalCreate':
            return 'Create local';
        case 'LocalEdit':
            return 'Edit local';
        // Fleet
        case 'FleetCreate':
            return 'Create vehicle';
        case 'FleetEdit':
            return 'Edit vehicle';
        // Staff
        case 'StaffCreate':
            return 'Create team member';
        case 'StaffEdit':
            return 'Edit team member';
        case 'CompanyStaffEdit':
            return 'Edit team member';
        // SMS & CALL
        case 'InboxCalls':
            return 'Calls';
        case 'InboxSms':
            return 'Inbox';
        // Comms
        case 'CommunicationCreate':
            return 'Create template';
        case 'CommunicationEdit':
            return 'Edit template';
        // Migration previews — reference the company being migrated
        case 'TenantMigrationPreview':
        case 'TenantMigrationWorkbookPreview': {
            const org = migrationStore.currentWorkbook?.tenant_name ?? migrationStore.currentWorkbook?.company_name;
            return org ? `${org} migration preview` : 'Migration Preview';
        }
        // Jobs
        case 'JobView':
            return jobStore?.data?.order_ref ? `Job #${jobStore?.data?.order_ref ?? ''}` : '';
        // Quotes
        case 'QuoteView':
            return tenderStore?.tender?.order_ref ? `Quote #${tenderStore?.tender?.order_ref ?? ''}` : '';
        // ICR
        case 'OrderInventoryConditionReport':
            return desktopView.value ? 'Inventory condition report' : 'ICR';
        case 'IcrSignOff':
            return 'ICR sign off';
        case 'IcrViewSignedOff':
            return route?.meta?.title;
        // Errors
        case '404Page':
            return '';
        default:
            return route?.meta?.title ?? capitalize(startCase(route.name));
    }
});

const showReturnToOrderLink = computed(() => {
    return ['OrderInventoryConditionReport', 'OrderInventory', 'IcrSignOff', 'IcrViewSignedOff'].includes(
        route.name ?? '',
    );
});

const showHistoryBackButton = computed(() => {
    return false;
});

const returnToOrderRoute = computed(() => {
    return { name: 'OrderView', params: { id: route.params.id } };
});

function setOrderViewActiveTabOnReturn() {
    try {
        const tab =
            ['OrderInventoryConditionReport', 'IcrSignOff', 'IcrViewSignedOff'].includes(route.name ?? '') ? 'ICR' : (
                'Inventory'
            );
        sessionStorage.setItem('orderView.activeTab', tab);
    } catch {
        // Intentionally ignore sessionStorage errors (e.g., in private mode)
    }
}

function goBackToPreviousPage() {
    router.push({
        name: 'IcrSignOff',
        params: { id: route.params.id },
    });
}
</script>
